import { getConsentSnapshot, registerConsentWithdrawalCleanup } from '@services/consent';
import { PENDING_SIGNUP_ENHANCED_CONVERSION_KEY } from '@services/enhancedConversions';

const ADS_COOKIE_PREFIXES = ['_gcl_', '_gac_'];

let initialized = false;
let unregisterAnalytics: (() => void) | null = null;
let unregisterAds: (() => void) | null = null;

function deleteCookie(name: string): void {
  const encodedName = encodeURIComponent(name);
  const domain = window.location.hostname.toLowerCase();
  const domainCandidates = new Set<string | undefined>([undefined, domain]);

  if (domain === 'sendallfiles.com' || domain.endsWith('.sendallfiles.com')) {
    domainCandidates.add('.sendallfiles.com');
  }

  for (const candidate of domainCandidates) {
    const domainPart = candidate ? `; Domain=${candidate}` : '';
    document.cookie = `${encodedName}=; Max-Age=0; Path=/${domainPart}; SameSite=Lax`;
  }
}

function deleteCookiesWithPrefixes(prefixes: readonly string[]): void {
  try {
    const names = document.cookie
      .split(';')
      .map((part) => part.split('=', 1)[0]?.trim())
      .filter((name): name is string => Boolean(name));

    for (const name of names) {
      const decoded = (() => {
        try {
          return decodeURIComponent(name);
        } catch {
          return name;
        }
      })();
      if (prefixes.some((prefix) => decoded.startsWith(prefix))) {
        deleteCookie(decoded);
      }
    }
  } catch {
    // Cookie access can be unavailable; every optional caller still remains disabled.
  }
}

function purgeAnalyticsCookies(): void {
  try {
    const names = document.cookie
      .split(';')
      .map((part) => part.split('=', 1)[0]?.trim())
      .filter((name): name is string => Boolean(name));

    for (const name of names) {
      const decoded = (() => {
        try {
          return decodeURIComponent(name);
        } catch {
          return name;
        }
      })();
      if (
        decoded.startsWith('ai_') ||
        decoded === '_ga' ||
        decoded.startsWith('_ga_') ||
        decoded === '_gid' ||
        decoded === '_gat' ||
        decoded.startsWith('_gat_')
      ) {
        deleteCookie(decoded);
      }
    }
  } catch {
    // Cookie access can be unavailable; every optional caller still remains disabled.
  }
}

function purgeAnalyticsStorage(): void {
  purgeAnalyticsCookies();
  try {
    const removeKnownAnalyticsKeys = (storage: Storage) => {
      const keys = Array.from({ length: storage.length }, (_, index) => storage.key(index))
        .filter((key): key is string => Boolean(key));
      for (const key of keys) {
        if (
          key === 'ai_session' ||
          key === 'ai_user' ||
          key === 'ai_authUser' ||
          key.startsWith('AI_buffer') ||
          key.startsWith('AI_sentBuffer')
        ) {
          storage.removeItem(key);
        }
      }
    };
    removeKnownAnalyticsKeys(sessionStorage);
    removeKnownAnalyticsKeys(localStorage);
  } catch {
    // Storage access can fail in hardened/private browser modes.
  }
}

function purgeAdsMeasurementStorage(): void {
  deleteCookiesWithPrefixes(ADS_COOKIE_PREFIXES);
  try {
    localStorage.removeItem('saf:ad-click-ids');
    localStorage.removeItem(PENDING_SIGNUP_ENHANCED_CONVERSION_KEY);
    const keys = Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index))
      .filter((key): key is string => Boolean(key));
    for (const key of keys) {
      if (key.startsWith('saf:signup-conversion:email-confirmed:')) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Attribution callers also fail closed independently.
  }
}

/** Installs first-explicit-denial and withdrawal cleanup hooks exactly once. */
export function initializePrivacyStorageCleanup(): void {
  if (initialized || typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }
  initialized = true;
  unregisterAnalytics = registerConsentWithdrawalCleanup('analytics', purgeAnalyticsStorage);
  unregisterAds = registerConsentWithdrawalCleanup('adsMeasurement', purgeAdsMeasurementStorage);

  // This first CMP release must also remove identifiers left by the previously unconditional
  // tags. Do not wait for a future change event: undecided, expired, and rejected visitors are
  // all denied by default and need their legacy optional state removed immediately.
  const snapshot = getConsentSnapshot();
  if (!snapshot.hasDecision || !snapshot.preferences.analytics) {
    purgeAnalyticsStorage();
  }
  if (!snapshot.hasDecision || !snapshot.preferences.adsMeasurement) {
    purgeAdsMeasurementStorage();
  }
}

/** Test-only reset for module state; not used by production code. */
export function resetPrivacyStorageCleanupForTests(): void {
  unregisterAnalytics?.();
  unregisterAds?.();
  unregisterAnalytics = null;
  unregisterAds = null;
  initialized = false;
}
